import {
  BookingType,
  IBlockout,
  IBookingLink,
  IBookingPayment,
  IBookingTag,
  ILayoutMinimalAbstract,
  ISavedBookingSelectedOptions,
  ISectionLayoutItem,
  ITableItem,
  ITableItemAbstract,
  TimeStatus,
  ICustomer,
  ILayoutMinimal,
  BookingMethods,
} from 'shared-types/SharedTypes';
import {
  IBookingCustomer,
  IScheduleAbstract,
  IScheduleServiceAbstract,
  IScheduleTime,
  IWidgetPaymentSummary,
} from 'shared-types/WidgetTypes';
import { IBookingStatus } from 'shared-types/bookingStatusTypes';
import { ManualJoinType } from 'shared-components/table-seater/types';

export interface IRecurringBooking {
  customer: ICustomer;
  bookingRepeat: RecurringBookingType;
  repeatTimes: number;
  startTime: string;
  endTime: string;
  repeatFrequency: number;
  id?: string;
}

/** Denotes the recurring booking type */
export enum RecurringBookingType {
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
  Daily = 'Daily',
}

export interface IWeeklyScheduleSubset {
  dayName: string;
  blockoutMessage: string;
  currentBookingsOnStandbyList: number;
  isVenueOpen: boolean;
  maxBookingsOnStandbyList: number;
  services: IGroupWidgetService[];
  tags: IBookingTag[];
}

export interface IRecurringBookingSubset {
  serviceId: string;
  serviceName?: string;
  sectionId: string;
  sectionName: string;
  tableIds: string[];
  numOfPeople: number;
  duration: string | number;
  deleteRecurringBooking?: boolean;
  time: string;
  _id?: string;
  index?: number;
  status?: IBookingStatus;
  tags?: IBookingTag[];
  notes?: string;
  bookingLinks?: IBookingLink[];
}

export interface IRecurringBookingRequest {
  recurringBooking: IRecurringBooking;
  bookings?: IRecurringBookingSubset[];
  bookingTemplate?: IRecurringBookingSubset;
}

export interface IRecurringBookingSaveRequest extends IRecurringBooking {
  bookingList: IRecurringBookingDetail[];
}

export interface IRecurringBookingDetail {
  isValid: boolean;
  reason: string;
  hasSelected: boolean;
  booking: IRecurringBookingSubset;
  bookings?: IRecurringBookingSubset[];
}

// For Recurring booking table picker

export interface ITableItemABC extends ITableItemAbstract {
  tables?: ITableItem[]; // this is a list of table ids, used when a table join exists. ABC passes this as table objects though.
}

export interface ILayoutMinimalABC extends ILayoutMinimalAbstract {
  tablesJoins: ITableItemABC[];
}
export interface IScheduleServiceABC extends IScheduleServiceAbstract {
  layouts?: ILayoutMinimalABC[];
}

export interface IGroupWidgetService extends IScheduleServiceABC {
  sectionLayouts: ISectionLayoutItem[];
  intervalsCustom: { times: string; values: string | number; intervalDuration: number }[];
  lastBookingTime: Date; // Fri Sep 04 2020 21:45:00 GMT+1000 (Australian Eastern Standard Time) {}
  name: string; // "All day"
  enableCustomPaxPerIntervals: boolean;
  maxCoversPerInterval: number;
  maxBookingsPerInterval: number;
  bookingInterval: number;
  firstBookingTime?: Date;
  type: string;
  _id: string; // @todo: sort out whether we're using id or _id - not both!
  startTime: Date;
  endTime: Date;
  bookingDuration: number;
}

export interface ITablePickerData {
  service: IGroupWidgetService;
  bookings: IRecurringBookingResponseData[];
  blockouts?: IBlockout[];
}

export interface IGroupWidgetSchedule extends IScheduleAbstract {
  services: IGroupWidgetService[];
  blockouts?: IBlockout[];
}

export interface IRecurringBookingScheduleSubset {
  services: IGroupWidgetService[];
  blockouts?: IBlockout[];
}

export interface IRecurringBookingResponseData {
  bookingRef: string; // "32582819"
  method: string; // "online"
  time: string; // "2019/11/30 09:30"
  orgTime: string; // "2019/11/30 09:30"
  duration: number; // 30
  people: number; // covers
  customer: IBookingCustomer;
  tables: ITableItem[];
  preferredSectionId: string; // "section_GIYOEZ9CC963K"
  tags: IBookingTag[];
  status: IBookingStatus;
  hasManagerNotes: boolean;
  hasCustomerNotes: boolean;
  bookedAt: string; // "2019-11-29T02:03:02.8191738+00:00"
  paymentSummary: IWidgetPaymentSummary; // null
  serviceId: string; // "service_SY3N885VPA6GG_1570589968666"
  lastModifiedDate: string; // "2019-11-29T02:03:02.8191738+00:00"
  serviceName: string; //  "Morning Tea"
  locked: boolean;
  onlineEditingDisabled: boolean;
  paymentPending: IBookingPayment; // null
  venueID: unknown;
  _id: string; // "booking_2019-11-30_ZDUVF7R0HU4D3_1575032582882"
  type: string; // "booking"
  bookingType: BookingType;
  selectedOptions: ISavedBookingSelectedOptions[];
  standByConfirmationExpiry: Date;

  // when customer is null, these may exist
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  phone?: string;
  notes?: string;
  onlineCancelDisabled?: boolean;
}

export interface IBookingSubset {
  status: IBookingStatus;
  tables: { _id?: string; shared?: boolean }[];
  time: Date;
  serviceId: string;
  _id: string;
  people: number;
  duration: number;
  bookingEnd?: Date;
}

export interface IScheduleSubset {
  /**
   * @todo: selectedSchedule on back end needs extra props on services
   * sectionLayouts: any[], type: string, _id: string, endTime: Date
   */

  services: {
    startTime: Date;
    sectionLayouts: ISectionLayoutItem[];
    type: string;
    _id: string;
    endTime: Date;
    times: IScheduleTime[];
  }[];
  blockouts?: IBlockout[];

  // compensating for stupid triple 'o' variable name in diary
  bookingInterval?: number;
  boookingInterval?: number;
}

export interface IServiceItemSubset {
  _id: string; // "service_MWNR2VZS6BAO1_1587075416493"
  type: string; // "AllDay", 'Event', 'event'
  startTime: Date; // Fri Sep 04 2020 06:00:00 GMT+1000 (Australian Eastern Standard Time) {}
  endTime: Date; // Fri Sep 04 2020 22:00:00 GMT+1000 (Australian Eastern Standard Time) {}

  sectionLayouts: ISectionLayoutItem[];
  intervalsCustom: { times: string; values: string | number; intervalDuration: number }[];
  lastBookingTime: Date; // Fri Sep 04 2020 21:45:00 GMT+1000 (Australian Eastern Standard Time) {}
  name: string; // "All day"

  enableCustomPaxPerIntervals: boolean;
  maxCoversPerInterval: number;
  maxBookingsPerInterval: number;

  boookingInterval?: number;
  bookingInterval?: number;
  firstBookingTime?: Date;

  times: IScheduleTime[];

  bookingDuration: number;
}

export interface IBookingInfoAtTime {
  time: Date;
  maxCovers: number;
  coversReached: number;
  bookingsAtTime: IBookingSubset[];
  vaildTablesAtTime: ITableItem[];
  freeTablesAtTime: ITableItem[];
  vaidJoinsAtTime: ITableItem[];
  invalidTablesAtTime: ITableItem[]; // introduced in booking-availability-service submodule
  status: TimeStatus;
  errorMgs: string[];
  warningMsgs: string[];
  open: boolean;
  service: string; // name of the service
}

export interface ITableAvailableLookUp {
  table: ITableItem;
  available: boolean;
  walkinTable: boolean;
}

export interface ITableSeaterRecurringProps {
  isWhiteTheme?: boolean; // this adds extra whiteness when useReversedColors is true
  useReversedColors?: boolean; // assumes the component is sitting on a dark colour, so all text, borders, etc are white
  manualJoinType?: ManualJoinType; // lock to a manualJoin or noJoin tyoe. If omitted the manualJoin button will appear
  label?: string;
  showEmptyTableValidationMessage?: boolean;
  classPrefix: string;
  tableSelectionList: ITableItem[];
  tableSelectionListWithJoins: ITableItem[];
  sectionsLookup: any; // object with dynamic keys
  preselectedTableIds: string[];
  booking?: IRecurringBookingSubset;
}

export interface ISectionDetails {
  id: string;
  name: string;
  order: number;
}

export interface IBookingAvailabilityService {
  getBookingsByTable: (bookings: IBookingSubset[]) => any;
  checkBookingsAtTime: (
    time: Date,
    bookingDate: Date,
    schedule: IScheduleSubset,
    service: IServiceItemSubset,
    layouts: ILayoutMinimal[],
    requestDuration: number,
    requestedPeople: number,
    tables: ITableItemAbstract[],
    bookingsByTable: any,
    preferredSectionId: string,
    requiredTableId: string,
    allowPassTimes: boolean,
    bookingMethod: BookingMethods,
    includeEventBlockedSectionIds: boolean
  ) => IBookingInfoAtTime;
}

export interface ITableSelectionListItem extends ITableItem {
  isAltBg?: boolean;
}
